import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MonthlyGraphPopup = ({ isOpen, onClose, monthlyData, filters }) => {
  const sellsChartRef = useRef(null);
  const priceChartRef = useRef(null);
  const gmvChartRef = useRef(null);

  const formatNumber = (num) => {
    return new Intl.NumberFormat("ru-RU").format(num);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthName = (month) => {
    const months = [
      "Янв",
      "Фев",
      "Мар",
      "Апр",
      "Май",
      "Июн",
      "Июл",
      "Авг",
      "Сен",
      "Окт",
      "Ноя",
      "Дек",
    ];
    return months[month - 1];
  };

  const formatDateLabel = (year, month) => {
    return `${getMonthName(month)} ${year}`;
  };

  const prepareChartData = () => {
    if (!monthlyData || !Array.isArray(monthlyData))
      return { labels: [], datasets: [] };

    const sortedData = [...monthlyData].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    const labels = sortedData.map((item) =>
      formatDateLabel(item.year, item.month)
    );

    return {
      labels,
      sellsData: sortedData.map((item) => item.sells_count || 0),
      priceData: sortedData.map((item) => item.avg_price || 0),
      gmvData: sortedData.map((item) => item.gmv || 0),
    };
  };

  const createChart = (canvasRef, data, label, color, yAxisLabel) => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");

    // Destroy existing chart if it exists
    if (canvasRef.current.chart) {
      canvasRef.current.chart.destroy();
    }

    canvasRef.current.chart = new ChartJS(ctx, {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: label,
            data: data.data,
            borderColor: color,
            backgroundColor: color + "20",
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: color,
            pointBorderColor: color,
            pointRadius: 4,
            pointHoverRadius: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "top",
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: function (context) {
                const value = context.parsed.y;
                if (label === "Средняя цена") {
                  return `${label}: ${formatCurrency(value)} ₸`;
                } else if (label === "GMV") {
                  return `${label}: ${formatCurrency(value)} ₸`;
                } else {
                  return `${label}: ${formatNumber(value)}`;
                }
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: "Месяц",
            },
            grid: {
              display: false,
            },
          },
          y: {
            display: true,
            title: {
              display: true,
              text: yAxisLabel,
            },
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                if (label === "Средняя цена" || label === "GMV") {
                  return formatCurrency(value);
                } else {
                  return formatNumber(value);
                }
              },
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
      },
    });
  };

  const getFiltersDisplayText = () => {
    const filterTexts = [];

    if (filters?.brand) filterTexts.push(`Бренд: ${filters.brand}`);
    if (filters?.product_name)
      filterTexts.push(`Товар: ${filters.product_name}`);
    if (filters?.root_category)
      filterTexts.push(`Категория 1: ${filters.root_category}`);
    if (filters?.category_2)
      filterTexts.push(`Категория 2: ${filters.category_2}`);
    if (filters?.category_3)
      filterTexts.push(`Категория 3: ${filters.category_3}`);
    if (filters?.category_4)
      filterTexts.push(`Категория 4: ${filters.category_4}`);
    if (filters?.from_date) filterTexts.push(`С: ${filters.from_date}`);
    if (filters?.to_date) filterTexts.push(`До: ${filters.to_date}`);

    return filterTexts.length > 0 ? filterTexts.join(", ") : "Все товары";
  };

  useEffect(() => {
    if (isOpen && monthlyData && Array.isArray(monthlyData)) {
      const chartData = prepareChartData();

      // Create charts after a short delay to ensure DOM is ready
      setTimeout(() => {
        createChart(
          sellsChartRef,
          { labels: chartData.labels, data: chartData.sellsData },
          "Количество продаж",
          "#3B82F6",
          "Количество"
        );

        createChart(
          priceChartRef,
          { labels: chartData.labels, data: chartData.priceData },
          "Средняя цена",
          "#10B981",
          "Цена (₸)"
        );

        createChart(
          gmvChartRef,
          { labels: chartData.labels, data: chartData.gmvData },
          "Выручка",
          "#F59E0B",
          "Выручка (₸)"
        );
      }, 100);
    }
  }, [isOpen, monthlyData]);

  if (!isOpen) return null;

  return (
    <div
      className="position-fixed w-100 h-100 d-flex align-items-start justify-content-center"
      style={{
        top: 0,
        left: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        paddingTop: "15vh",
        zIndex: 1050,
        animation: "fadeIn 0.3s ease-out",
      }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-lg overflow-hidden"
        style={{
          maxWidth: "90vw",
          maxHeight: "80vh",
          width: "1000px",
          animation: "slideIn 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-4 py-3 border-bottom position-relative"
          style={{ paddingBottom: "16px" }}
        >
          <h4 className="mb-0">Месячная аналитика</h4>
          <h6 className="text-muted mb-3">{getFiltersDisplayText()}</h6>
          <button
            className="btn btn-link position-absolute p-2"
            style={{
              top: "16px",
              right: "20px",
              color: "#6b7280",
              border: "none",
              background: "transparent",
              borderRadius: "6px",
              transition: "all 0.2s ease",
            }}
            onClick={onClose}
            aria-label="Закрыть"
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#f3f4f6";
              e.target.style.color = "#374151";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "#6b7280";
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div
          className="p-4 overflow-auto"
          style={{
            maxHeight: "calc(80vh - 120px)",
            scrollbarWidth: "thin",
            scrollbarColor: "#c1c1c1 #f1f1f1",
          }}
        >
          <div>
            <div
              className="mb-4"
              style={{ height: "300px", position: "relative" }}
            >
              <canvas ref={sellsChartRef}></canvas>
            </div>

            <div
              className="mb-4"
              style={{ height: "300px", position: "relative" }}
            >
              <canvas ref={priceChartRef}></canvas>
            </div>

            <div style={{ height: "300px", position: "relative" }}>
              <canvas ref={gmvChartRef}></canvas>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        /* Custom scrollbar for webkit browsers */
        div::-webkit-scrollbar {
          width: 6px;
        }

        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        div::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        div::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default MonthlyGraphPopup;
